import { useEffect, useState } from 'react'
import { getActiveTheme, setThemePreference, onThemeChange, type Theme } from '@/app/theme'

function SunIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" className={className} aria-hidden="true">
      <circle cx="12" cy="12" r="4" />
      <path d="M12 3v1.5M12 19.5V21M4.93 4.93l1.06 1.06M17.66 17.66l1.06 1.06M3 12h1.5M19.5 12H21M4.93 19.07l1.06-1.06M17.66 6.34l1.06-1.06" />
    </svg>
  )
}

function MoonIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M20.354 15.354A9 9 0 0 1 8.646 3.646 9.003 9.003 0 1 0 20.354 15.354Z" />
    </svg>
  )
}

/**
 * Manual light/dark toggle. The legacy app never had one - theme.js only
 * ever followed the OS setting - but layering an explicit override on top
 * doesn't change that default: nobody sees any difference until they
 * actually click this. A sliding pill switch (not a plain icon button) so
 * the current state reads at a glance without needing to hover for a
 * tooltip, with the inactive icon dimmed in the track rather than hidden -
 * the standard "which way will this go" affordance for a two-state toggle.
 */
export default function ThemeToggle() {
  const [theme, setTheme] = useState<Theme>(getActiveTheme)

  useEffect(() => onThemeChange(setTheme), [])

  const isDark = theme === 'dark'

  return (
    <button
      type="button"
      role="switch"
      aria-checked={isDark}
      aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      onClick={() => setThemePreference(isDark ? 'light' : 'dark')}
      className="relative inline-flex h-7 w-14 shrink-0 items-center rounded-full border border-border bg-bg-tertiary transition-colors duration-200 hover:border-brand/50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
    >
      <SunIcon className={`absolute left-1.5 h-3.5 w-3.5 transition-colors duration-200 ${isDark ? 'text-text-tertiary' : 'text-brand'}`} />
      <MoonIcon className={`absolute right-1.5 h-3.5 w-3.5 transition-colors duration-200 ${isDark ? 'text-brand' : 'text-text-tertiary'}`} />
      <span
        aria-hidden="true"
        className={`relative z-10 inline-block h-6 w-6 rounded-full bg-card shadow transition-transform duration-200 ease-out ${
          isDark ? 'translate-x-[1.875rem]' : 'translate-x-0.5'
        }`}
      />
    </button>
  )
}
